/**
 * Choosing an exchange rate to quote a customer at — and saying how old it is.
 *
 * ---- Why this is not a one-line lookup ----
 *
 * `data_ops.get_exchange_rates` returns the `Currency Exchange` rows as they are, and measured on
 * 2026-08-08 they are messier than a single number per pair:
 *
 *   USD -> AED  3.65   2024-10-28  buy=0 sell=1     two live answers for one pair,
 *   USD -> AED  3.67   2023-12-12  buy=1 sell=0     differing by 0.5%
 *   AED -> BHD  0.1    2023-01-01                   two again
 *   AED -> BHD  0.1027 2022-08-01
 *   SAR -> AED  0.973                               1/0.973 = 1.0277 …
 *   AED -> SAR  1.02                                … so a round trip loses ~0.7%
 *
 * Newest overall was 2026-07-23 against a system date of 2026-08-08 — sixteen days — and several
 * rows date from 2022. So a rate is picked deliberately, and the caller is told the date so a human
 * can judge it.
 *
 * ---- The rules ----
 *
 * 1. **SELLING.** A customer quotation is us selling, so `for_selling` rows win. That is a
 *    commercial choice, not arithmetic: for USD->AED it is the difference between 3.65 and 3.67.
 * 2. **Newest wins** among equally-suitable rows.
 * 3. **No inversion, ever.** The pairs above are not reciprocal, so deriving AED->SAR from SAR->AED
 *    would invent a rate the business never set. A missing direction is reported as missing.
 * 4. **No cross-rates.** Nothing is routed through a third currency for the same reason.
 *
 * A caller that cannot get a rate must not quote in that currency — see
 * `quotationFromAllocation.ts`, which refuses rather than falling back to 1.0. A quotation priced
 * at an invented rate looks exactly like a correct one.
 */

import type { WireFx } from './api/wire'

/** One `Currency Exchange` row, as `data_ops.get_exchange_rates` sends it. */
export interface ExchangeRateRow {
  from_currency: string
  to_currency: string
  exchange_rate: number
  /** `"YYYY-MM-DD"`. */
  date: string
  for_buying: number | boolean
  for_selling: number | boolean
}

export interface PickedRate {
  rate: number
  /** `"YYYY-MM-DD"` — shown to the user, because a rate's age is part of whether to trust it. */
  date: string
  /** True when no `for_selling` row existed and a buying rate was used instead. */
  buyingFallback: boolean
}

const truthy = (v: number | boolean | undefined): boolean => v === 1 || v === true

/**
 * The rate to convert `from` into `to`, or `null`.
 *
 * `null` means "we do not have this rate", which is a fact the caller must act on rather than
 * paper over — never `1`, which silently prices a foreign quotation at par.
 */
export function pickRate(rows: readonly ExchangeRateRow[], from: string, to: string): PickedRate | null {
  const a = from.trim().toUpperCase()
  const b = to.trim().toUpperCase()
  // Same currency needs no rate and no row — and must not be reported as missing.
  if (a !== '' && a === b) return { rate: 1, date: '', buyingFallback: false }
  if (a === '' || b === '') return null

  const direct = rows.filter(
    (r) =>
      r.from_currency?.trim().toUpperCase() === a &&
      r.to_currency?.trim().toUpperCase() === b &&
      Number.isFinite(r.exchange_rate) &&
      r.exchange_rate > 0,
  )
  if (direct.length === 0) return null

  const newest = (list: readonly ExchangeRateRow[]) =>
    [...list].sort((x, y) => (y.date ?? '').localeCompare(x.date ?? ''))[0]

  const selling = direct.filter((r) => truthy(r.for_selling))
  const chosen = selling.length > 0 ? newest(selling) : newest(direct)
  return {
    rate: chosen.exchange_rate,
    date: chosen.date ?? '',
    // Said out loud: a buying rate on a customer quotation is the wrong side of the spread, and
    // the caller should show it rather than let it pass as the selling rate.
    buyingFallback: selling.length === 0,
  }
}

/** Whole days between a `"YYYY-MM-DD"` rate date and today, or `null` when undated. */
export function rateAgeDays(date: string, today: Date): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((date ?? '').trim())
  if (!m) return null
  const then = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const days = Math.floor((today.getTime() - then.getTime()) / 86_400_000)
  return days < 0 ? 0 : days
}

/**
 * Past this, a rate is worth questioning on screen. A month is deliberately generous — the point
 * is to catch the 2022 rows, not to nag about a fortnight.
 */
export const STALE_AFTER_DAYS = 30

export function isStale(date: string, today: Date): boolean {
  const age = rateAgeDays(date, today)
  return age !== null && age > STALE_AFTER_DAYS
}

/**
 * "selling rate, as at 2026-07-23 (16 days ago)" — shown next to any converted figure.
 *
 * The SIDE is stated even when it is the expected one. USD→AED has both a selling rate (3.65) and
 * a buying rate (3.67) on file, so "which side did this use" is a real question with a real answer,
 * and a converted total that does not say invites the reader to assume. Naming it only on the
 * fallback would mean silence carries the meaning, which is exactly how the wrong side goes
 * unnoticed.
 */
export function describeRate(picked: PickedRate, today: Date): string {
  if (picked.date === '') return 'same currency — no conversion'
  const age = rateAgeDays(picked.date, today)
  const agePart = age === null ? '' : age === 0 ? ' (today)' : ` (${age} day${age === 1 ? '' : 's'} ago)`
  const side = picked.buyingFallback ? 'BUYING rate — no selling rate on file' : 'selling rate'
  return `${side}, as at ${picked.date}${agePart}`
}

/**
 * The `Currency Exchange` table as a `WireFx` display snapshot.
 *
 * The screens convert through `lib/fx.ts`'s `convert`, which reads a `WireFx` — so a screen given
 * no snapshot has no rates at all, and `convert` silently returns the money unchanged. That is how
 * the quote screen's currency picker came to move only the total's LABEL while every figure on it
 * stayed put.
 *
 * EVERY DIRECTION WE HOLD IS EMITTED, deliberately. `wire.ts`'s `fxLookup` falls back to `1/rate`
 * when it finds no direct pair, and on this table inverting is wrong — AED->SAR 1.02 inverts to
 * 0.98 where the business set 0.973. Listing both directions means the direct hit always wins, so
 * that inversion branch never fires for a pair we actually have.
 *
 * Same `pickRate` rule as the quotation build, so a screen cannot show one number while the
 * quotation is priced at another.
 */
export function toWireFx(rows: readonly ExchangeRateRow[]): WireFx {
  const pairs = new Set<string>()
  for (const row of rows) {
    const from = row.from_currency?.trim().toUpperCase()
    const to = row.to_currency?.trim().toUpperCase()
    if (from && to && from !== to) pairs.add(`${from} ${to}`)
  }

  const fx: WireFx = {}
  for (const pair of pairs) {
    const [from, to] = pair.split(' ')
    const picked = pickRate(rows, from, to)
    if (picked !== null) fx[`${from}_${to}`] = { rate: picked.rate, date: picked.date }
  }
  return fx
}
